import CurvaRisco from "./CurvaRisco";
import InsightsProduto from "./InsightsProduto";
import { useState, useMemo } from "react";
import { jStat } from "jstat";

type Props = {
  mediaHist: number;
  sigma: number;
  onChangeMeta?: (meta: number) => void;
};

export default function SimuladorMeta({ mediaHist, sigma }: Props) {
  const [meta, setMeta] = useState(mediaHist);

  // 📌 Meta aproximada (regra dos 3σ)
  const metaSeguraAprox = useMemo(() => 95 + 3 * sigma, [sigma]);

  // 📌 Meta exata (usando CDF inversa, risco ≤ 0.01%)
  const metaSeguraExata = useMemo(() => {
    // quantil z da normal padrão para probabilidade de 0.01%
    const z = jStat.normal.inv(0.0001, 0, 1);
    return 95 - z * sigma;
  }, [sigma]);

  // 📌 Economia estimada (comparando meta atual vs meta exata)
  const economia = useMemo(() => {
    return meta > metaSeguraExata ? meta - metaSeguraExata : 0;
  }, [meta, metaSeguraExata]);

  return (
    <div className="mt-6 bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <h3 className="font-semibold mb-3">🎯 Simulador de Meta</h3>

      {/* Slider */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="range"
          min={mediaHist - 10}
          max={mediaHist + 10}
          step={0.1}
          value={meta}
          onChange={(e) => setMeta(parseFloat(e.target.value))}
          className="w-full accent-sky-600"
        />
        <span className="text-sm font-semibold">{meta.toFixed(1)} g</span>
      </div>

      {/* Curva de risco + legenda */}
      <CurvaRisco mediaHist={mediaHist} meta={meta} sigma={sigma} />

      {/* Sugestão automática */}
      <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
        <p>
          ✅ Meta sugerida sem risco de multa (cálculo estatístico):{" "}
          <b>{metaSeguraExata.toFixed(1)} g</b>
        </p>
        {economia > 0 ? (
          <p>
            Ajustar de {meta.toFixed(1)} g para {metaSeguraExata.toFixed(1)} g gera
            uma economia estimada de <b>{economia.toFixed(2)} g/unidade</b>.
          </p>
        ) : (
          <p>Já está operando em nível seguro (0% de risco).</p>
        )}
        <p className="text-xs text-slate-500 mt-2">
          🔎 Referência simplificada (regra de 3σ): {metaSeguraAprox.toFixed(1)} g
        </p>
      </div>

      {/* Insights dentro do mesmo card */}
      <InsightsProduto mediaHist={mediaHist} meta={meta} sigma={sigma} />
    </div>
  );
}
